import React, { KeyboardEventHandler, ReactEventHandler } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { useWtrSelector } from 'redux/hooks';
import isEqual from 'lodash/isEqual';

import { getBuilderGroupByIndex } from 'components/Builder/selectors';
import { Typography } from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { KEY_ENTER } from 'constants/keys';
import { BaseGroupItemPlaceholder, BuilderVariant } from 'components/Builder/types';
import { EMPTY_BUILDER_ITEMS } from 'components/MealDealPage/constants';
import LineItem from './LineItem';

import styles from './LineView.scss';

interface BuilderLineViewProps {
  builderIndex: number;
  collapsed: boolean;
  GroupItemPlaceholder: BaseGroupItemPlaceholder;
  isMobile?: boolean;
  groupIndex: number;
  name?: string;
  onSlotClick?: ReactEventHandler;
  promotionId?: string;
  variant: BuilderVariant;
  groupId?: string;
  builderId?: string;
}

const builderLineViewDefaultPropOnSlotClick = () => {};

const BuilderLineView = ({
  builderIndex,
  collapsed,
  isMobile = false,
  groupIndex,
  GroupItemPlaceholder,
  name,
  promotionId,
  builderId,
  variant,
  groupId,
  onSlotClick = builderLineViewDefaultPropOnSlotClick,
}: BuilderLineViewProps) => {
  const { items = EMPTY_BUILDER_ITEMS, threshold } = useWtrSelector(
    state =>
      getBuilderGroupByIndex(state, { promotionId, index: builderIndex, variant, groupIndex }),
    isEqual,
  );
  const completed = items.filter((item: unknown) => !!item).length >= threshold;

  const mobileMessage = completed ? ', click to change or remove' : `, add a ${name || 'product'}`;
  const accessibleLabel = `${name ? `${name} ` : ''}${completed ? 'completed' : 'incomplete'}${
    isMobile ? `${mobileMessage}` : ''
  }`;

  const handleKeyDown: KeyboardEventHandler<HTMLButtonElement> = event => {
    const { keyCode } = event;

    if (keyCode === KEY_ENTER) {
      onSlotClick(event);
    }
  };

  return (
    <div className={classNames(styles.builderLine)} data-testid="builder-line">
      {name ? (
        <div data-testid="lineTitleWrapper" className={styles.lineTitleWrapper}>
          <div className={styles.line} />
          <Typography
            className={styles.lineTitle}
            element="h3"
            styleAs="sectionHeadingSmall"
            noMargins
            fixedSize
          >
            {name}
          </Typography>
          <div className={styles.line} />
        </div>
      ) : null}
      <ul className={styles.items}>
        {items.map((item: { lineNumber: string }, index: number) => (
          <li
            key={uuidv4()}
            className={classNames(styles.lineItem, {
              [styles.product]: item,
            })}
          >
            <button
              className={classNames(styles.buttonItem)}
              type="button"
              onClick={onSlotClick}
              onKeyDown={handleKeyDown}
              aria-label={accessibleLabel}
              tabIndex={collapsed ? -1 : 0}
              data-lineitem={`${builderId}-group-${groupId}-slot-${index}`}
            >
              {item ? (
                <LineItem
                  lineNumber={item.lineNumber}
                  data-testid={`builder-line-item-${item.lineNumber}`}
                />
              ) : (
                <GroupItemPlaceholder
                  completed={completed}
                  index={index}
                  isMobile={isMobile}
                  items={items}
                />
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

BuilderLineView.displayName = 'BuilderLineView';

export default BuilderLineView;
